import React, { useId, useMemo, useState } from 'react'
import {
  ArrowSquareOut,
  BookOpenText,
  CaretDown,
  Clock,
  Envelope,
  Info,
  Lightbulb,
  Lifebuoy,
  MagnifyingGlass,
  PlayCircle,
  X,
} from '@phosphor-icons/react'
import { Card } from '@/components/ui/Card'
import { Button, buttonVariants } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { useSettingsStore } from '@/stores/settingsStore'
import {
  cardChromeClass,
  inputClass,
  inputStyle,
  infoBannerClass,
  bannerTextStyle,
  pillBaseClass,
  pillActiveClass,
  pillInactiveClass,
  pillTextStyle,
} from '@/lib/styleKit'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import { HELP_MODULES, type HelpArticle, type HelpModule } from '../helpCentre/helpContent'
import { GUIDE_VIDEOS, type GuideVideo } from '../helpCentre/guideVideos'
import { filterHelpModules } from '../helpCentre/helpSearch'
import { toEmbedUrl, toThumbnailUrl } from '../helpCentre/videoEmbed'

/**
 * Settings > Help Centre.
 *
 * Two panels behind one segmented control: the written Support & FAQ (every
 * article from the Help Centre document, grouped by module) and the Guide
 * videos (placeholders until each recording's link is pasted into
 * `guideVideos.ts`). Read-only and visible to every role: help is not a
 * permission.
 *
 * The FAQ is searched locally (nothing leaves the browser) and a match is
 * opened as it is found, so an answer is on screen without a second click.
 * Every module and article also carries a `data-settings-anchor`
 * (`help-<id>`) so the settings search box can deep-link into it.
 */

type HelpTab = 'faq' | 'videos'

const SUPPORT_EMAIL = 'support@beevelope.com'

interface HelpCentreSectionProps {
  /** Overridable for tests; defaults to the shipped document content. */
  modules?: HelpModule[]
  videos?: GuideVideo[]
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 590,
  letterSpacing: '-0.288px',
  lineHeight: 1.33,
}
const subtitleStyle: React.CSSProperties = { fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }
const bodyStyle: React.CSSProperties = { fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.6 }

const defaultOpenIds = (modules: HelpModule[]): Set<string> => {
  const first = modules[0]?.articles[0]
  return new Set(first ? [first.id] : [])
}

const HelpCentreSection: React.FC<HelpCentreSectionProps> = ({
  modules = HELP_MODULES,
  videos = GUIDE_VIDEOS,
}) => {
  const [tab, setTab] = useState<HelpTab>('faq')
  const setActiveSection = useSettingsStore((s) => s.setActiveSection)
  const baseId = useId()
  const tabId = (t: HelpTab) => `${baseId}-tab-${t}`
  const panelId = (t: HelpTab) => `${baseId}-panel-${t}`

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>
            Help Centre
          </h2>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={subtitleStyle}>
            Step-by-step answers and guide videos for every part of Beevelope.
          </p>
        </div>

        <div
          className="inline-flex p-1 gap-1 rounded-md bg-muted/50 border border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06] flex-shrink-0"
          role="tablist"
          aria-label="Help Centre section"
          onKeyDown={(e) =>
            handleTabListKeyDown(e, {
              activeIndex: tab === 'faq' ? 0 : 1,
              count: 2,
              onSelect: (i) => setTab(i === 0 ? 'faq' : 'videos'),
            })
          }
        >
          {(
            [
              { key: 'faq', label: 'Support & FAQ', Icon: BookOpenText },
              { key: 'videos', label: 'Guide videos', Icon: PlayCircle },
            ] as const
          ).map(({ key, label, Icon }) => {
            const active = tab === key
            return (
              <button
                key={key}
                type="button"
                role="tab"
                id={tabId(key)}
                aria-controls={panelId(key)}
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                onClick={() => setTab(key)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3.5 py-1 rounded border transition-colors duration-150',
                  active
                    ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
                )}
                style={{ fontSize: '13px', fontWeight: active ? 590 : 510, letterSpacing: '-0.13px' }}
              >
                <Icon className="h-4 w-4" weight="regular" />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {tab === 'faq' ? (
        <div role="tabpanel" id={panelId('faq')} aria-labelledby={tabId('faq')}>
          <FaqPanel modules={modules} onOpenFeedback={() => setActiveSection('feedback-support')} />
        </div>
      ) : (
        <div role="tabpanel" id={panelId('videos')} aria-labelledby={tabId('videos')}>
          <VideosPanel videos={videos} />
        </div>
      )}
    </div>
  )
}

export default HelpCentreSection

/* ── Support & FAQ ─────────────────────────────────────────────────────── */

const FaqPanel: React.FC<{ modules: HelpModule[]; onOpenFeedback: () => void }> = ({ modules, onOpenFeedback }) => {
  const [query, setQuery] = useState('')
  const [moduleId, setModuleId] = useState<string | null>(null)
  const [openIds, setOpenIds] = useState<Set<string>>(() => defaultOpenIds(modules))

  const visible = useMemo(() => filterHelpModules(modules, { query, moduleId }), [modules, query, moduleId])

  // A search opens what it finds; clearing it returns to the resting state.
  const applyQuery = (next: string, nextModuleId: string | null = moduleId) => {
    setQuery(next)
    if (next.trim()) {
      const matched = filterHelpModules(modules, { query: next, moduleId: nextModuleId })
      setOpenIds(new Set(matched.flatMap((m) => m.articles.map((a) => a.id))))
    } else {
      setOpenIds(defaultOpenIds(modules))
    }
  }

  const pickModule = (id: string | null) => {
    setModuleId(id)
    if (query.trim()) applyQuery(query, id)
  }

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-[#62666d]" />
        <input
          type="search"
          aria-label="Search help articles"
          placeholder="Search help articles, e.g. warm-up, Apollo, credits"
          value={query}
          onChange={(e) => applyQuery(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          className={cn(inputClass, 'pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden')}
          style={inputStyle}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => applyQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.06] transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5" aria-label="Filter by module">
        <ModulePill label="All" active={moduleId === null} onClick={() => pickModule(null)} />
        {modules.map((m) => (
          <ModulePill key={m.id} label={m.title} active={moduleId === m.id} onClick={() => pickModule(m.id)} />
        ))}
      </div>

      {visible.length === 0 ? (
        <Card className={cn(cardChromeClass, 'p-10 text-center')}>
          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.15px' }}>
            {`No articles match "${query.trim()}".`}
          </p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1.5" style={subtitleStyle}>
            Try a feature name such as Apollo, warm-up or credits, or pick a module above.
          </p>
          <Button variant="outline" size="sm" className="mt-5" onClick={() => applyQuery('')}>
            Clear search
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {visible.map((module) => (
            <ModuleCard key={module.id} module={module} openIds={openIds} onToggle={toggle} />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap rounded-lg border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] px-5 py-4">
        <div className="min-w-0">
          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
            Still stuck?
          </p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12.5px', letterSpacing: '-0.1px', lineHeight: 1.5 }}>
            Tell us what you were trying to do and what happened. We read every message.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Beevelope support')}`}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-2')}
          >
            <Envelope className="h-4 w-4" />
            Email support
          </a>
          {/* Not a real <a> -- it navigates within the app (Settings >
              Feedback & support) rather than leaving it, but role="link"
              matches that it's taking you somewhere, not submitting
              anything, which button-variant chrome alone doesn't convey. */}
          <button
            type="button"
            role="link"
            onClick={onOpenFeedback}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-2')}
          >
            <Lifebuoy className="h-4 w-4" />
            Feedback &amp; support
          </button>
        </div>
      </div>
    </div>
  )
}

const ModulePill: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(pillBaseClass, active ? pillActiveClass : pillInactiveClass)}
    style={pillTextStyle}
  >
    {label}
  </button>
)

const ModuleCard: React.FC<{
  module: HelpModule
  openIds: Set<string>
  onToggle: (id: string) => void
}> = ({ module, openIds, onToggle }) => (
  <Card className={cn(cardChromeClass, 'overflow-hidden')} padding="none">
    <div className="px-5 pt-4 pb-3.5" data-settings-anchor={`help-${module.id}`}>
      <h3 className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.18px', lineHeight: 1.4 }}>
        {module.title}
      </h3>
      {module.description && (
        <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={subtitleStyle}>
          {module.description}
        </p>
      )}
    </div>
    <div>
      {module.articles.map((article) => (
        <ArticleRow key={article.id} article={article} open={openIds.has(article.id)} onToggle={() => onToggle(article.id)} />
      ))}
    </div>
  </Card>
)

const ArticleRow: React.FC<{ article: HelpArticle; open: boolean; onToggle: () => void }> = ({ article, open, onToggle }) => {
  const bodyId = `help-article-${article.id}`
  return (
    <div className="border-t border-border/50 dark:border-white/[0.06]" data-settings-anchor={`help-${article.id}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left transition-colors duration-150 hover:bg-muted/40 dark:hover:bg-white/[0.03]"
      >
        <span
          className={cn('min-w-0 truncate', open ? 'text-foreground dark:text-[#f7f8f8]' : 'text-foreground/85 dark:text-[#d0d6e0]')}
          style={{ fontSize: '13.5px', fontWeight: 510, letterSpacing: '-0.14px' }}
        >
          {article.title}
        </span>
        <CaretDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div id={bodyId} className="px-5 pb-5 pt-0.5 space-y-3.5">
          {article.whatItIs && (
            <p className="text-foreground/85 dark:text-[#d0d6e0]" style={bodyStyle}>
              {article.whatItIs}
            </p>
          )}
          {article.steps && article.steps.length > 0 && (
            <ol className="space-y-2">
              {article.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-5 w-5 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-muted/70 dark:bg-white/[0.05] text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                    style={{ fontSize: '11px', fontWeight: 510 }}
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                  <span className="text-foreground/85 dark:text-[#d0d6e0]" style={bodyStyle}>
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {article.table && <ReferenceTable table={article.table} />}
          {article.callout && <Callout tone={article.callout.tone} text={article.callout.text} />}
        </div>
      )}
    </div>
  )
}

const Callout: React.FC<{ tone: 'tip' | 'note'; text: string }> = ({ tone, text }) => {
  const isTip = tone === 'tip'
  const Icon = isTip ? Lightbulb : Info
  return (
    <div
      className={cn(
        isTip
          ? infoBannerClass
          : 'flex items-start gap-2 px-3 py-2.5 rounded-md border bg-muted/40 border-border/60 text-muted-foreground dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#8a8f98]',
      )}
    >
      <Icon className="h-4 w-4 mt-px flex-shrink-0" weight={isTip ? 'fill' : 'regular'} />
      <p style={bannerTextStyle}>
        <span className="mr-1.5" style={{ fontWeight: 590 }}>{isTip ? 'Tip' : 'Note'}</span>
        <span className={isTip ? 'text-foreground/85 dark:text-[#d0d6e0]' : undefined}>{text}</span>
      </p>
    </div>
  )
}

const ReferenceTable: React.FC<{ table: NonNullable<HelpArticle['table']> }> = ({ table }) => (
  <div className="overflow-x-auto rounded-md border border-border/50 dark:border-white/[0.06]">
    <table className="w-full border-collapse" style={{ fontSize: '12.5px', letterSpacing: '-0.1px', lineHeight: 1.5 }}>
      <thead>
        <tr className="bg-muted/40 dark:bg-white/[0.02]">
          {table.columns.map((col, i) => (
            <th
              key={col}
              scope="col"
              className={cn(
                'text-left px-3 py-2 text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap',
                i === 0 ? 'w-[28%]' : undefined,
              )}
              style={{ fontSize: '11.5px', fontWeight: 510 }}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.groups.map((group, gi) => (
          <React.Fragment key={group.title ?? gi}>
            {group.title && (
              <tr>
                <td
                  colSpan={table.columns.length}
                  className="px-3 pt-3 pb-1 text-foreground dark:text-[#f7f8f8] border-t border-border/50 dark:border-white/[0.06]"
                  style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                >
                  {group.title}
                </td>
              </tr>
            )}
            {group.rows.map((row) => (
              <tr key={row[0]} className="border-t border-border/40 dark:border-white/[0.05]">
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={cn(
                      'px-3 py-2.5 align-top',
                      ci === 0
                        ? 'text-foreground dark:text-[#f7f8f8] whitespace-nowrap'
                        : 'text-foreground/80 dark:text-[#d0d6e0]',
                    )}
                    style={ci === 0 ? { fontWeight: 510 } : undefined}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
)

/* ── Guide videos ──────────────────────────────────────────────────────── */

/** Run of consecutive entries sharing one `heading` (possibly none). */
interface VideoGroup {
  heading?: string
  videos: GuideVideo[]
}

// Consecutive entries fold into one group as long as `heading` stays the
// same string (every member of a group repeats it — see `guideVideos.ts`); a
// changed (or newly-set, or newly-cleared) heading starts a fresh group.
// This keeps an unheaded list (every existing test fixture) rendering as a
// single ungrouped grid, exactly as before.
function groupByHeading(videos: GuideVideo[]): VideoGroup[] {
  const groups: VideoGroup[] = []
  for (const video of videos) {
    const current = groups[groups.length - 1]
    if (current && current.heading === video.heading) {
      current.videos.push(video)
    } else {
      groups.push({ heading: video.heading, videos: [video] })
    }
  }
  return groups
}

const VideosPanel: React.FC<{ videos: GuideVideo[] }> = ({ videos }) => (
  <div className="space-y-6">
    {groupByHeading(videos).map((group, gi) => (
      <div key={group.heading ?? `ungrouped-${gi}`}>
        {group.heading && (
          <h3
            className="text-foreground dark:text-[#f7f8f8] mb-3"
            style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.18px' }}
          >
            {group.heading}
          </h3>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {group.videos.map((video, i) => (
            <VideoCard key={video.id} video={video} featured={i === 0} />
          ))}
        </div>
      </div>
    ))}
  </div>
)

const VideoCard: React.FC<{ video: GuideVideo; featured: boolean }> = ({ video, featured }) => {
  const [playing, setPlaying] = useState(false)
  // Hidden again if the image fails to load (offline, blocked host), so the
  // card falls back to the plain play button rather than a broken image.
  const [thumbBroken, setThumbBroken] = useState(false)
  const embed = video.url ? toEmbedUrl(video.url) : null
  const thumbnail = video.url && !thumbBroken ? toThumbnailUrl(video.url) : null

  return (
    <Card
      className={cn(cardChromeClass, 'overflow-hidden flex flex-col', featured && 'sm:col-span-2')}
      padding="none"
      data-testid={`guide-video-${video.id}`}
    >
      <div
        className={cn(
          'relative w-full bg-muted/50 dark:bg-white/[0.03] border-b border-border/50 dark:border-white/[0.06]',
          featured ? 'aspect-[21/9]' : 'aspect-video',
        )}
      >
        {!video.url ? (
          <>
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayCircle className="h-12 w-12 text-muted-foreground/35 dark:text-white/[0.12]" weight="thin" />
            </div>
            <span
              className="absolute left-3 top-3 inline-flex items-center h-6 px-2.5 rounded-full border bg-background/90 text-muted-foreground border-border/60 dark:bg-[#191a1b]/90 dark:text-[#8a8f98] dark:border-white/[0.08]"
              style={pillTextStyle}
            >
              Coming soon
            </span>
          </>
        ) : embed ? (
          playing ? (
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`${embed}${embed.includes('?') ? '&' : '?'}autoplay=1`}
              title={video.title}
              loading="lazy"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              // The site is served with `Referrer-Policy: same-origin`, which
              // strips the Referer from this cross-origin frame request. The
              // YouTube player requires one to configure itself and otherwise
              // shows "Video player configuration error (Error 153)". Sending
              // the origin only (never the path) is what YouTube needs.
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <button
              type="button"
              aria-label={`Play ${video.title}`}
              onClick={() => setPlaying(true)}
              className="group absolute inset-0 flex items-center justify-center transition-colors duration-150 hover:bg-[#5e6ad2]/[0.04] dark:hover:bg-[#5e6ad2]/[0.08]"
            >
              {thumbnail && (
                <>
                  {/* The video's own poster frame; the dark wash keeps the
                      play button readable over any frame. */}
                  <img
                    src={thumbnail}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    draggable={false}
                    onError={() => setThumbBroken(true)}
                    className="absolute inset-0 h-full w-full object-cover select-none"
                    data-testid="guide-video-thumbnail"
                  />
                  <span aria-hidden="true" className="absolute inset-0 bg-black/25 transition-colors duration-150 group-hover:bg-black/35" />
                </>
              )}
              <PlayCircle
                className={cn(
                  'relative h-14 w-14 transition-transform duration-150 group-hover:scale-105',
                  thumbnail ? 'text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]' : 'text-[#5e6ad2] dark:text-[#a5b4fc]',
                )}
                weight="fill"
              />
            </button>
          )
        ) : (
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Watch ${video.title}`}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-[#5e6ad2]/[0.04] dark:hover:bg-[#5e6ad2]/[0.08] transition-colors duration-150"
          >
            <ArrowSquareOut className="h-8 w-8" />
            <span style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.05px' }}>Watch video</span>
          </a>
        )}
      </div>
      <div className="px-4 py-3.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.15px' }}>
            {video.title}
          </p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12.5px', letterSpacing: '-0.1px', lineHeight: 1.5 }}>
            {video.description}
          </p>
        </div>
        {video.duration && (
          <span
            className="flex-shrink-0 inline-flex items-center gap-1 text-muted-foreground dark:text-[#8a8f98] tabular-nums"
            style={{ fontSize: '11px', fontWeight: 510 }}
          >
            <Clock className="h-3 w-3" />
            {video.duration}
          </span>
        )}
      </div>
    </Card>
  )
}
